import axios from "axios";
import classNames from "classnames";
import { SERVER_URL } from "Constants";
import useAsync from "Hooks/useAsync";
import { store } from "Models/Store";
import moment from "moment";
import { useState } from "react";
import { catchErrorAsync } from "Util/CatchErrorAsync";
import HandleEvents from "Util/HandleEvents";
import alertToast from "Util/ToastifyUtils";
import { Badge } from "Views/Components/badge/badge";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { DirectorDetailSection } from "./DirectorDetailSection";
import { MonitoredDirectorSummary } from "./DirectorSectionTypes";

const fetchMonitoredDirectorSummary = async (customerId: string) => {
	const apiCall = axios.get<MonitoredDirectorSummary[]>(`${SERVER_URL}/api/directors/customer/${customerId}`);
	const [error, data] = await catchErrorAsync(apiCall);
	if (error) {
		alertToast("Error fetching monitored director summary");
		console.error(error);
		return [];
	}
	return data.data;
}

type Props = {
	customerId: string;
}

export const CustomerMonitoredDirectorInfo: React.FunctionComponent<Props> = ({ customerId }) => {
	const [showDirectorInfo, setShowDirectorInfo] = useState(true);

	const [showDirectorDetail, setShowDirectorDetail] = useState<Record<string, boolean>>({});

	const response = useAsync(async () => {
		const data = await fetchMonitoredDirectorSummary(customerId);
		return data;
	}, [customerId]);

	const renderDate = (date: Date) => {
		if (!date) {
			return "N/A";
		}

		return moment(date).format("Do MMMM YYYY");
	}

	if (response.type === 'loading') {
		return <InlineSpinner />;
	}

	if (!response.data || response.data.length === 0) {
		return null;
	}

	const monitoredSummary = response.data;
	return (
		<>
			<div className='section-title-container'>
				<span>
					<h6 className="section-title">Director Information</h6>
				</span>
				<span
					className={classNames('accordion-icon icon-only icon-chevron-down', {
						open: showDirectorInfo
					})}
					{...HandleEvents(() => setShowDirectorInfo(prev => !prev))}
				/>
			</div>
			{showDirectorInfo && (
				<div className="director-information">
					<div className="accordion-group">
						{monitoredSummary.map(monitored => (
							<div className="accordion-item" key={monitored.monitoredId}>
								<div className="accordion-title-container">
									<h6 className="accordion-title-header">
										{monitored.entityName} ({monitored.businessNumber})
										{monitored.totalDirectorCount > 0 && (<Badge variant="info" morph="rounded-rectangular">{monitored.totalDirectorCount}</Badge>)}
									</h6>
									<span className="accordion-title-info">Last ASIC Extract on file: {renderDate(monitored.asicExtractDate)}</span>
									<span
										className="accordion-title-info icon-only icon-refresh-cw"
										{...HandleEvents(() => store.routerHistory.push("/hub/credit-reports/generate", {
											stage: "review",
											customerId,
											monitoredId: monitored.monitoredId,
										}))}
									/>
									<span
										className={classNames('accordion-title-info accordion-icon icon-only icon-chevron-down', {
											open: showDirectorDetail[monitored.monitoredId],
										})}
										{...HandleEvents(() => setShowDirectorDetail(prev => ({
											...prev,
											[monitored.monitoredId]: !prev[monitored.monitoredId]
										})))}
									/>
								</div>
								<div className={classNames("accordion-content", {
									expanded: showDirectorDetail[monitored.monitoredId],
									collapsed: !showDirectorDetail[monitored.monitoredId]
								})}>
									{showDirectorDetail[monitored.monitoredId] && (
										<>
											{monitored.totalDirectorCount === 2 && (
												<div className="accordion-item">
													<p className="alert-box info">
														Please note there are no ASIC directors on file.&nbsp;
														<span
															style={{cursor: "pointer", textDecoration: "underline", fontWeight: "600"}}
															{...HandleEvents(() => store.routerHistory.push("/hub/credit-reports/generate", { stage: "review", customerId: customerId, monitoredId: monitored.monitoredId,}))}>Click here</span>&nbsp;to refresh directors from ASIC
													</p>
												</div>
											)}
											{monitored.totalDirectorCount > 0 && (
												<DirectorDetailSection monitoredId={monitored.monitoredId}/>
											)}
										</>
									)}
								</div>
							</div>
						))}
						<p style={{marginTop:"10px"}}><strong>Disclaimer</strong>: The officeholders listed were accurate at the time of the extract but may no longer hold these positions at the time of any company status change.</p>
					</div>
				</div>
			)}
		</>
	)
}





